import PropTypes from 'prop-types';
import { format } from 'date-fns';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider
} from '@mui/material';

import ThermalContentHeader from './ThermalContentHeader';
import { numberToWords, resolvePharmacyInvoiceTotals, adjustGstSummaryForRoundOff, formatPrice } from './utils';
import {
  calculateInclusiveLineGst,
  summarizeInclusiveGst,
  getItemGstPercentage,
  getExplicitGstAmounts
} from 'utils/gst';
export default function PharmacyInvoiceThermalContent({ customerInfo, invoiceData, hospitalData }) {



  if (!invoiceData) {
    return null;
  }

  const {
    items: { pharmacy = [] } = {}
  } = invoiceData;

  const summaryMode = invoiceData?.pharmacy_gst_summary?.mode;
  const inferredMode = pharmacy.some(item => Number(item?.cgst || item?.cgst_amount || 0) > 0 || Number(item?.sgst || item?.sgst_amount || 0) > 0)
    ? 'cgst_sgst'
    : 'none';
  const gstMode = summaryMode === 'cgst_sgst' ? 'cgst_sgst' : inferredMode;
  const showGst = gstMode === 'cgst_sgst';

  const processedItems = () => {
    if (!pharmacy.length) return { activeItems: [], cancelledItems: [] };
    const hasCancelledItems = pharmacy.some(item => item.status === 'cancelled');
    const hasStatusField = pharmacy.some(item => item.status);

    const normalizeItem = (item) => {
      const unitPrice = parseFloat(item.selling_price ?? item.price ?? 0);
      return {
        ...item,
        price: unitPrice,
        selling_price: unitPrice,
        quantity: Number(item.quantity ?? item.qty ?? item.cancel_quantity ?? 0)
      };
    };

    if (!hasStatusField) {
      return { activeItems: pharmacy.map(normalizeItem), cancelledItems: [] };
    }

    if (hasCancelledItems) {
      const itemsMap = {};
      pharmacy.forEach(rawItem => {
        const item = normalizeItem(rawItem);
        const itemId = item.id;
        if (!itemsMap[itemId]) {
          itemsMap[itemId] = {
            id: itemId,
            name: item.name,
            hsn_code: item.hsn_code || '',
            price: item.price,
            selling_price: item.selling_price,
            activeQuantity: 0,
            cancelledQuantity: 0,
            discount_in_perc: item.discount_in_perc || 0,
            gst_percentage: getItemGstPercentage(item),
            batches: Array.isArray(item.batches) ? [...item.batches] : []
          };
        } else {
          const lineDisc = parseFloat(item.discount_in_perc || 0);
          const prevDisc = parseFloat(itemsMap[itemId].discount_in_perc || 0);
          if (lineDisc > prevDisc) {
            itemsMap[itemId].discount_in_perc = lineDisc;
          }
        }

        if (item.status === 'active') {
          itemsMap[itemId].activeQuantity = Number(item.quantity);
        } else if (item.status === 'cancelled') {
          itemsMap[itemId].cancelledQuantity += Number(item.quantity);
        }

        if (Array.isArray(item.batches) && item.batches.length > 0) {
          itemsMap[itemId].batches = [...(itemsMap[itemId].batches || []), ...item.batches];
        }
      });

      const activeItems = Object.values(itemsMap)
        .map(item => ({
          ...item,
          quantity: item.activeQuantity - item.cancelledQuantity
        }))
        .filter(item => item.quantity > 0);

      return { activeItems, cancelledItems: pharmacy.filter(item => item.status === 'cancelled').map(normalizeItem) };
    }

    return {
      activeItems: pharmacy.filter(item => item.status === 'active').map(normalizeItem),
      cancelledItems: []
    };
  };

  const { activeItems, cancelledItems } = processedItems();

  const computedLineDiscountTotal = activeItems.reduce((sum, item) => {
    const unit = parseFloat(item.selling_price || item.price || 0);
    const qty = Number(item.quantity || 1);
    return sum + (unit * qty * parseFloat(item.discount_in_perc || 0)) / 100;
  }, 0);

  const gstSummary = summarizeInclusiveGst({
    items: activeItems.map(item => ({
      ...item,
      batches: [],
      cgst_amount: 0,
      sgst_amount: 0,
      igst_amount: 0,
      cgst: 0,
      sgst: 0,
      igst: 0
    })),
    gstEnabled: showGst,
    mode: gstMode
  });

  const subtotalBeforeDiscount = Number(invoiceData.actualSubtotal ?? invoiceData.original_amount ?? 0);
  const discountPercent = Number(invoiceData.discount || 0);
  const explicitActualDiscount =
    invoiceData.actualDiscount != null && invoiceData.actualDiscount !== ''
      ? Number(invoiceData.actualDiscount)
      : null;
  const totalDiscountAmount = Number(
    computedLineDiscountTotal > 0
      ? computedLineDiscountTotal
      : (explicitActualDiscount !== null
          ? explicitActualDiscount
          : (subtotalBeforeDiscount > 0 ? (subtotalBeforeDiscount * discountPercent) / 100 : 0))
  );
  const anyDiscount =
    pharmacy.some(item => parseFloat(item.discount_in_perc || 0) > 0) || totalDiscountAmount > 0;

  const resolvedTotals = resolvePharmacyInvoiceTotals({
    netTotal: gstSummary.totalAmount
  });
  const roundOff = Number(invoiceData.roundOff ?? resolvedTotals.roundOffDisplay);
  const isPositiveRoundOff = typeof invoiceData.isPositiveRoundOff === 'boolean'
    ? invoiceData.isPositiveRoundOff
    : resolvedTotals.isPositiveRoundOff;
  const finalAmountToPay = resolvedTotals.grandTotal;
  const displayGstSummary = adjustGstSummaryForRoundOff(gstSummary, finalAmountToPay) ?? gstSummary;
  const totalInWords = `Rs. ${numberToWords(finalAmountToPay)} Only`;

  // Group cancelled items by return event (cancellation_ledger_id)
  const groupedCancelledEvents = (() => {
    if (!cancelledItems || cancelledItems.length === 0) return [];
    
    const eventsMap = {};
    cancelledItems.forEach(item => {
      const ledgerId = item.cancellation_ledger_id || 'unknown';
      const timeStr = item.created_at ? format(new Date(item.created_at), 'dd-MM-yyyy HH:mm') : 'N/A';
      const staffName = item.cancelled_by_name || 'N/A';

      if (!eventsMap[ledgerId]) {
        eventsMap[ledgerId] = {
          time: timeStr,
          staffName: staffName,
          items: []
        };
      }
      eventsMap[ledgerId].items.push(item);
    });
    return Object.values(eventsMap);
  })();

  // Hoist grand cancel total so billing summary can also reference it
  const grandCancelTotal = groupedCancelledEvents.reduce((gsum, evt) =>
    gsum + evt.items.reduce((sum, item) => {
      const price = parseFloat(item.selling_price || item.price || 0);
      const qty = parseInt(item.quantity || 1, 10);
      const discount = parseFloat(item.discount_in_perc || 0);
      const explicitAmounts = getExplicitGstAmounts(item);
      const lineSummary = calculateInclusiveLineGst({
        grossUnitPrice: price, quantity: qty, discountPercentage: discount,
        taxPercentage: getItemGstPercentage(item), gstEnabled: showGst, mode: gstMode,
        explicitCgstAmount: explicitAmounts.cgstAmount,
        explicitSgstAmount: explicitAmounts.sgstAmount,
        explicitIgstAmount: explicitAmounts.igstAmount
      });
      return sum + lineSummary.totalAmount;
    }, 0), 0);

  return (
    <Box sx={{
      maxWidth: "72mm",
      width: "72mm",
      color: '#000',
      fontSize: "9px",
      "& .MuiTableCell-root": { p: "2px", fontSize: "11px", color: '#000 !important' },
      "& .MuiTypography-root": { color: '#000 !important' },
      '@media print': {
        fontSize: '11px',
        boxSizing: 'border-box',
      },
    }}>
      <ThermalContentHeader customerInfo={customerInfo} invoiceData={invoiceData} showGstDetails={showGst} />
      <TableContainer>
        <Table size="small"
          sx={{
            '& .MuiTableCell-root': {
              fontSize: '10px',
            },
            '& .MuiTableCell-head': {
              fontSize: '10px',
              fontWeight: 600
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell style={{ paddingLeft: '0', width: "8%" }}>#</TableCell>
              <TableCell style={{ width: "36%" }}>Description</TableCell>
              <TableCell align="center" style={{ width: "20%" }}>Qty x Rate</TableCell>
              <TableCell align="center" style={{ width: "12%" }}>MRP</TableCell>
              <TableCell align="center" style={{ width: "10%" }}>Disc</TableCell>
              <TableCell align="center" style={{ paddingRight: '0', width: "14%" }}>Final</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeItems.map((item, i) => {
              const price = parseFloat(item.selling_price || item.price || 0);
              const qty = parseInt(item.quantity || 1, 10);
              const discount = parseFloat(item.discount_in_perc || 0);
              const grossAmount = price * qty;
              const gstPercentage = getItemGstPercentage(item);
              const explicitAmounts = getExplicitGstAmounts(item);
              const lineSummary = calculateInclusiveLineGst({
                grossUnitPrice: price,
                quantity: qty,
                discountPercentage: discount,
                taxPercentage: gstPercentage,
                gstEnabled: showGst,
                mode: gstMode,
                explicitCgstAmount: explicitAmounts.cgstAmount,
                explicitSgstAmount: explicitAmounts.sgstAmount,
                explicitIgstAmount: explicitAmounts.igstAmount
              });

              return (
                <>
                  <TableRow key={`active-main-${item.id || item.name}-${i}`}>
                    <TableCell style={{ paddingLeft: '0', verticalAlign: 'top' }}>{i + 1}</TableCell>
                    <TableCell style={{
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      maxWidth: '24ch',
                      verticalAlign: 'top'
                    }}>
                      <Typography sx={{ fontSize: '10px', fontWeight: 600 }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ verticalAlign: 'top', fontSize: '9px', whiteSpace: 'nowrap' }}>{`${qty} x ${formatPrice(price)}`}</TableCell>
                    <TableCell align="center" sx={{ verticalAlign: 'top' }}>{grossAmount.toFixed(2)}</TableCell>
                    <TableCell align="center" sx={{ verticalAlign: 'top' }}>{`${discount.toFixed(2)}%`}</TableCell>
                    <TableCell align="center" style={{ paddingRight: '0', verticalAlign: 'top', fontWeight: 600 }}>
                      {lineSummary.totalAmount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow key={`active-meta-${item.id || item.name}-${i}`}>
                    <TableCell style={{ paddingLeft: '0', textAlign: 'right' }} colSpan={6}>
                      <Typography sx={{ fontSize: '9px', textAlign: 'right' }}>
                        {showGst ? `HSN: ${item.hsn_code || '-'} | ` : ''}
                        {Array.isArray(item.batches) && item.batches.length > 0
                          ? ` | ${item.batches.map(b =>
                              `B: ${b.batch_number || '-'} (Exp: ${b.expiry_date ? format(new Date(b.expiry_date), 'MM/yy') : '-'})`
                            ).join(', ')}`
                          : ` | ${item.batch_number ? `B: ${item.batch_number}` : ''}${item.batch_number && item.expiry_date ? ' | ' : ''}Exp: ${item.expiry_date ? format(new Date(item.expiry_date), 'MM/yy') : '-'}`
                        }
                        {showGst && gstMode === 'cgst_sgst'
                          ? ` | CGST: ${lineSummary.cgstPercentage.toFixed(2)}% | SGST: ${lineSummary.sgstPercentage.toFixed(2)}%`
                          : ''}
                        {showGst && gstMode === 'igst'
                          ? ` | IGST: ${lineSummary.igstPercentage.toFixed(2)}%`
                          : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {groupedCancelledEvents.length > 0 && (() => {
        return (
          <>
            <Divider sx={{ my: 0.5, borderColor: 'black' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
                SALES RETURNS ({groupedCancelledEvents.length})
              </Typography>
              <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
                Total: Rs {grandCancelTotal.toFixed(2)}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '9px', p: '1px 2px', color: '#000' } }}>
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 'bold', borderBottom: '1px solid black' } }}>
                    <TableCell style={{ paddingLeft: '0', width: '6%' }}>#</TableCell>
                    <TableCell style={{ width: '40%' }}>Name</TableCell>
                    <TableCell align="center" style={{ width: '12%' }}>Qty</TableCell>
                    <TableCell align="center" style={{ width: '18%' }}>Rate</TableCell>
                    <TableCell align="right" style={{ paddingRight: '0', width: '24%' }}>Amt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedCancelledEvents.map((evt, evtIdx) => {
                    const eventTotal = evt.items.reduce((sum, item) => {
                      const price = parseFloat(item.selling_price || item.price || 0);
                      const qty = parseInt(item.quantity || 1, 10);
                      const discount = parseFloat(item.discount_in_perc || 0);
                      const explicitAmounts = getExplicitGstAmounts(item);
                      const lineSummary = calculateInclusiveLineGst({
                        grossUnitPrice: price, quantity: qty, discountPercentage: discount,
                        taxPercentage: getItemGstPercentage(item), gstEnabled: showGst, mode: gstMode,
                        explicitCgstAmount: explicitAmounts.cgstAmount,
                        explicitSgstAmount: explicitAmounts.sgstAmount,
                        explicitIgstAmount: explicitAmounts.igstAmount
                      });
                      return sum + lineSummary.totalAmount;
                    }, 0);

                    return (
                      <>
                        {/* Compact event header — single row, no box */}
                        <TableRow key={`evt-hdr-${evtIdx}`} sx={{ backgroundColor: '#f0f0f0' }}>
                          <TableCell colSpan={3} style={{ paddingLeft: '0', fontStyle: 'italic' }}>
                            #{evtIdx + 1} {evt.time}
                          </TableCell>
                          <TableCell colSpan={2} align="right" style={{ paddingRight: '0', fontStyle: 'italic', fontSize: '8px' }}>
                            By: {evt.staffName || 'N/A'}
                          </TableCell>
                        </TableRow>
                        {evt.items.map((item, i) => {
                          const price = parseFloat(item.selling_price || item.price || 0);
                          const qty = parseInt(item.quantity || 1, 10);
                          const discount = parseFloat(item.discount_in_perc || 0);
                          const explicitAmounts = getExplicitGstAmounts(item);
                          const lineSummary = calculateInclusiveLineGst({
                            grossUnitPrice: price, quantity: qty, discountPercentage: discount,
                            taxPercentage: getItemGstPercentage(item), gstEnabled: showGst, mode: gstMode,
                            explicitCgstAmount: explicitAmounts.cgstAmount,
                            explicitSgstAmount: explicitAmounts.sgstAmount,
                            explicitIgstAmount: explicitAmounts.igstAmount
                          });
                          return (
                            <TableRow key={`cancel-item-${evtIdx}-${i}`}>
                              <TableCell style={{ paddingLeft: '0' }}>{i + 1}</TableCell>
                              <TableCell style={{ overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: '20ch' }}>
                                {item.name}
                              </TableCell>
                              <TableCell align="center">{qty}</TableCell>
                              <TableCell align="center">{formatPrice(price)}</TableCell>
                              <TableCell align="right" style={{ paddingRight: '0', fontWeight: 600 }}>
                                {lineSummary.totalAmount.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Event subtotal — only if there are multiple events */}
                        {groupedCancelledEvents.length > 1 && (
                          <TableRow key={`evt-total-${evtIdx}`}>
                            <TableCell colSpan={4} align="right" style={{ fontStyle: 'italic' }}>sub:</TableCell>
                            <TableCell align="right" style={{ paddingRight: '0', fontWeight: 'bold' }}>
                              Rs {eventTotal.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                  {/* Grand cancel total row */}
                  <TableRow sx={{ borderTop: '1px solid black' }}>
                    <TableCell colSpan={4} align="right" style={{ fontWeight: 'bold', paddingTop: '2px' }}>
                      Total Returned:
                    </TableCell>
                    <TableCell align="right" style={{ paddingRight: '0', fontWeight: 'bold', paddingTop: '2px' }}>
                      Rs {grandCancelTotal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      })()}


      <Divider sx={{ my: 0.5, borderColor: 'black' }} />
      <Box sx={{ textAlign: 'left', fontSize: '11px' }}>
        {/* Subtotal */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mb: 0.5,
          mt: 0.5,
          py: 0.5,
          px: 0.5,
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid black',
          borderBottom: '1px solid black'
        }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>Subtotal (Before Discount)</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>
            Rs {subtotalBeforeDiscount.toFixed(2)}
          </Typography>
        </Box>

        {/* Discount */}
        {anyDiscount && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ fontSize: '11px' }}>Discount</Typography>
            <Typography sx={{ fontSize: '11px' }}>
              - Rs{totalDiscountAmount.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Cancelled Items deduction */}
        {cancelledItems.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ fontSize: '11px' }}>(-) Cancelled Items</Typography>
            <Typography sx={{ fontSize: '11px' }}>
              Rs {grandCancelTotal.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Round Off */}
        {roundOff > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ fontSize: '11px' }}>Round Down</Typography>
            <Typography sx={{ fontSize: '11px' }}>
              {isPositiveRoundOff ? '+' : '-'}Rs{roundOff.toFixed(2)}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 0.3, borderStyle: 'dashed' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>
            Grand Total
          </Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>
            Rs{finalAmountToPay.toFixed(2)}
          </Typography>
        </Box>

        {showGst && (
          <Box sx={{ opacity: 0.8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography sx={{ fontSize: '11px' }}>Taxable Value</Typography>
              <Typography sx={{ fontSize: '11px' }}>
                Rs{displayGstSummary.taxableValue.toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.3 }}>
              <Box>
                <Typography sx={{ fontSize: '11px' }}>
                  Included Tax
                </Typography>
                <Typography sx={{ fontSize: '10px', lineHeight: 1.2 }}>
                  {displayGstSummary.mode === 'igst'
                    ? `IGST: Rs ${displayGstSummary.igstAmount.toFixed(2)}`
                    : `CGST: Rs ${displayGstSummary.cgstAmount.toFixed(2)}, SGST: Rs ${displayGstSummary.sgstAmount.toFixed(2)}`}
                </Typography>
              </Box>

              <Typography sx={{ fontSize: '11px' }}>
                Rs{displayGstSummary.totalTax.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Typography align="center" sx={{ fontStyle: 'italic' }}>
        {totalInWords}
      </Typography>

      <Typography
        align="center"
        sx={{
          width: '100%',
          fontSize: '11px',
          fontWeight: 600,
          flexShrink: 0,
          textAlign: 'center',
          mt: 1,
          pb: 1,
          '@media print': {
            paddingTop: 0.5,
            paddingBottom: '2mm'
          },
        }}
      >
        Authorised Signatory
      </Typography>
    </Box>
  );
}

PharmacyInvoiceThermalContent.propTypes = {
  customerInfo: PropTypes.object.isRequired,
  invoiceData: PropTypes.object.isRequired
};
